import { Fragment, type MouseEvent, useEffect, useState } from 'react';
import { BankAccountApi } from '@recargas-dominicanas/core/api';
import { Pagination, Table, TableHeader, Text } from '@recargas-dominicanas/core/components';
import { OptionButton } from '@recargas-dominicanas/core/components';
import { PendingTransactionItem } from './pending-transaction-item/pending-transaction-item.component';
import { BankScrapingManagerStore } from '../../../store/bankScrapingManager';
import { BankAccountStore } from '../../../store/bankAccountStore';
import { ErrorPopup } from './error-popup/error-popup.component';
import { Status } from './status/status.component';
import { style } from './pending-transactions.module.css';

interface Props {
  id: number;
}

export function PendingTransactions(props: Props) {

  const { id } = props;

  const page = BankAccountStore.getPendingTransactions(id);
  const bankScrapingService = BankAccountStore.getLastBankScrapingService(id);
  const nextExecution = BankScrapingManagerStore.getNextExecution();
  const [refreshing, setRefreshing] = useState(false);
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [disabledExecutionButton, setDisableExecutionButton] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executionFailed, setExecutionFailed] = useState(false);

  useEffect(() => {
    update();
  }, [id]);

  useEffect(() => {
    setDisableExecutionButton(bankScrapingService?.status === 'pending' || bankScrapingService?.status === 'running');
  }, [bankScrapingService?.status]);

  useEffect(() => {

    let id: NodeJS.Timeout;

    if (bankScrapingService?.status === 'pending' || bankScrapingService?.status === 'running') {
      id = setInterval(update, 2500);
    }

    return () => {
      clearInterval(id);
    };

  }, [bankScrapingService?.status]);

  async function handleFetch(page: number, size: number) {
    await BankAccountStore.fetchPendingTransactions(id, page, size);
  }

  async function handleRefreshStatus() {
    setRefreshing(true);
    setExecutionFailed(false);
    await update();
    setRefreshing(false);
  }

  async function handleStartScrapingService() {

    try {

      setExecuting(true);
      setExecutionFailed(false);
      setDisableExecutionButton(true);
      await BankAccountApi.startScrapingService(id);
      const bankScrapingManager = await BankAccountApi.getLastBankScrapingManager(id);
      const status = bankScrapingManager.bankScrapingServices?.[0].status;
      BankAccountStore.updateBankScrapingManager(id, bankScrapingManager);
      setExecuting(false);
      setDisableExecutionButton(status === 'pending' || status === 'running');
      
    } catch {
      
      setExecuting(false);
      setExecutionFailed(true);
      setDisableExecutionButton(false);
    }
  }

  function handleOpenErrorPopup(event: MouseEvent<HTMLDivElement>) {
    setOpenErrorPopup(open => !open);
    event.stopPropagation();
  }

  async function update() {
    await Promise.all([
      BankAccountStore.fetchLastBankScrapingManager(id),
      BankAccountStore.fetchPendingTransactions(id, 1, 25),
      BankScrapingManagerStore.fetchNextExecution()
    ]);
  }

  function formatTime(date: string) {

    const day = getDate(new Date(date));
    const today = getDate(new Date());
    const yesterday = getDate(new Date(), -1);
    const tomorrow = getDate(new Date(), 1);
    const timeFormatted = new Intl.DateTimeFormat('es-DO', { timeStyle: 'short' }).format( new Date(date));

    if (day === yesterday) return `ayer ${timeFormatted}`;
    if (day === today) return `hoy ${timeFormatted}`;
    if (day === tomorrow) return `mañana ${timeFormatted}`;

    return new Intl.DateTimeFormat('es-DO', { dateStyle: 'short', timeStyle: 'short' }).format( new Date(date));
  }

  function getDate(date: Date, day = 0) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + day, 0, 0, 0, 0).getTime();
  }

  return (
    <div className={style.container}>
      <div className={style.toolbar}>
        <div className={style.toolbarContent}>
          {bankScrapingService &&
            <span className={style.text}>Últ. ejecución: {formatTime(bankScrapingService.date)}</span>
          }
          {bankScrapingService && nextExecution &&
            <Fragment>
              {Date.parse(nextExecution.date) >= Date.now() &&
                <span className={style.text}>Próx. ejecución: {formatTime(nextExecution.date)}</span>
              }
              {Date.parse(nextExecution.date) < Date.now() &&
                <span className={style.text}>Próx. ejecución: no establecida</span>
              }
            </Fragment>
          }
          {!bankScrapingService &&
            <span className={style.text}>No se ha ejecutado el servicio</span>
          }
          {bankScrapingService?.status === 'failed' &&
            <div className={style.statusErrorContainer}>
              <Status 
                icon='error_outline' 
                title='Error' 
                severity='error' 
                onClick={handleOpenErrorPopup}/>
              <ErrorPopup
                open={openErrorPopup}
                onClose={() => setOpenErrorPopup(false)}>
                {bankScrapingService.error}
              </ErrorPopup>
            </div>
          }
          {executionFailed &&
            <Status icon='error_outline' title='Error ejecutando servicio' severity='error'/>
          }
          {executing &&
            <Status icon='autorenew' title='Ejecutando' rotatedIcon={true}/>
          }
          {(bankScrapingService?.status === 'pending' || bankScrapingService?.status === 'running') &&
            <Status icon='autorenew' title='En progreso' rotatedIcon={true}/>
          }
          {refreshing &&
            <Status icon='autorenew' title='Actualizando' rotatedIcon={true}/>
          }
        </div>
        <div className={style.toolbarContent}>
          <OptionButton 
            icon='refresh' 
            title='Actualizar'
            onClick={handleRefreshStatus}/>
          <OptionButton 
            icon='play_arrow' 
            title='Ejecutar'
            disabled={disabledExecutionButton}
            onClick={handleStartScrapingService}/>
        </div>
      </div>
      <div className={style.tableContainer}>
        <Table style={style.table}>
          <TableHeader 
            style={style.tableHeader} 
            autoHide={true}>
            <Text text='Tipo'/>
            <Text text='Balance'/>
            <Text text='Fecha'/>
            <Text text='Estado'/>
            <Text text='Descripción'/>
            <Text text=''/>
          </TableHeader>
          <tbody>
            {
              page?.data.map(pendingTransaction => 
                <PendingTransactionItem 
                  key={pendingTransaction.id}
                  bankAccountId={id}
                  pendingTransaction={pendingTransaction}/>
              )
            }
          </tbody>
        </Table>
      </div>
      {page && 
        <Pagination
          title='Transacciones'
          index={page.index}
          pages={page.pages}
          count={page.count}
          size={page.size}
          onChange={handleFetch}/>
      }
    </div>
  );
}
